import React, { useState, useEffect, useContext } from 'react';
import { X, Rocket, Plus, CheckCircle, AlertTriangle, Loader2, ChevronRight, ChevronLeft, Info, Search, Zap } from 'lucide-react';
import api from '../api/api';
import { SnackbarContext } from '../App';
import { Button } from './ui/button';

/**
 * AddToCampaignModal
 *
 * An inline non-redirecting modal that lets operators:
 *   1. SELECT an existing campaign
 *   2. CREATE a new campaign inline (lightweight)
 *   3. See a RESULT summary with added / duplicate / failed counts
 *
 * Props:
 *   workspaceContactIds  string[]   The workspace-local contact _ids to add
 *   onClose              () => void Called when the modal should close
 *   onComplete           () => void Called after a successful add (optional)
 */
export default function AddToCampaignModal({
    isOpen,
    workspaceContactIds = [],
    onClose,
    onComplete
}) {
    const showSnackbar = useContext(SnackbarContext);

    // Steps: SELECT | CREATE | ADDING | RESULT
    const [step, setStep] = useState('SELECT');

    // Campaign listing
    const [campaigns, setCampaigns] = useState([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);

    // Inline create form
    const [newName, setNewName] = useState('');
    const [createError, setCreateError] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    // Results
    const [results, setResults] = useState(null);

    // ─── Fetch active campaigns ───────────────────────────────────────────
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const load = async () => {
            setLoadingCampaigns(true);
            try {
                const res = await api.get('/api/campaigns/active');
                setCampaigns(Array.isArray(res.data) ? res.data : []);
            } catch {
                setCampaigns([]);
            } finally {
                setLoadingCampaigns(false);
            }
        };
        load();
        return undefined;
    }, [isOpen]);

    // If modal is not open, do not render
    if (!isOpen) return null;

    // ─── Filtered campaigns ───────────────────────────────────────────────
    const filteredCampaigns = campaigns.filter(c =>
        !search || c.name.toLowerCase().includes(search.toLowerCase())
    );

    // ─── Inline create ────────────────────────────────────────────────────
    const handleCreateCampaign = async () => {
        if (!newName.trim()) { setCreateError('Campaign name is required'); return; }
        setCreateError('');
        setIsCreating(true);
        try {
            const res = await api.post('/api/campaigns', {
                name: newName.trim(),
                status: 'DRAFT',
                startMode: 'IMMEDIATE'
            });
            const created = res.data;
            // Add to the local list and auto-select it
            setCampaigns(prev => [created, ...prev]);
            setSelectedCampaignId(created._id);
            setStep('SELECT'); // Go back to select step with the new campaign pre-selected
            showSnackbar(`Campaign "${created.name}" created`, 'success');
        } catch (err) {
            setCreateError(err.response?.data?.error || err.response?.data?.message || 'Failed to create campaign');
        } finally {
            setIsCreating(false);
        }
    };

    // ─── Add contacts to campaign ─────────────────────────────────────────
    const handleAddContacts = async () => {
        if (!selectedCampaignId) return;
        setStep('ADDING');
        try {
            const res = await api.post(`/api/campaigns/${selectedCampaignId}/recipients`, {
                workspaceContactIds,
                allowRisky: true,
                allowUnknown: true
            });
            setResults(res.data.results);
            setStep('RESULT');
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to add contacts', 'error');
            setStep('SELECT');
        }
    };

    // ─── Helpers ──────────────────────────────────────────────────────────
    const campaignStatusColor = (status) => {
        if (status === 'RUNNING') return 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20';
        if (status === 'PAUSED') return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
        if (status === 'DRAFT') return 'bg-violet-500/10 text-violet-700 border-violet-500/20';
        return 'bg-muted text-muted-foreground border-border';
    };

    const selectedCampaign = campaigns.find(c => c._id === selectedCampaignId);

    // ─── RESULT step ──────────────────────────────────────────────────────
    if (step === 'RESULT' && results) {
        return (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="bg-card border-2 border-border/40 w-full max-w-md rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.1)] overflow-hidden animate-in zoom-in-95 duration-400">
                    <div className="p-8 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-emerald-500/10 rounded-3xl flex items-center justify-center mb-6 shadow-inner">
                            <CheckCircle className="w-8 h-8 text-emerald-600" />
                        </div>
                        <h3 className="text-2xl font-black tracking-tight text-foreground mb-1">Done!</h3>
                        <p className="text-muted-foreground text-sm font-medium mb-8">
                            Contacts processed for <span className="font-black text-foreground">{selectedCampaign?.name || 'campaign'}</span>
                        </p>

                        <div className="w-full grid grid-cols-3 gap-3 mb-8">
                            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4 flex flex-col items-center gap-1">
                                <span className="text-2xl font-black text-emerald-700">{results.added ?? 0}</span>
                                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-700 opacity-70">Added</span>
                            </div>
                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 flex flex-col items-center gap-1">
                                <span className="text-2xl font-black text-amber-700">{results.skipped_duplicate ?? 0}</span>
                                <span className="text-[10px] font-black uppercase tracking-widest text-amber-700 opacity-70">Already In</span>
                            </div>
                            <div className="bg-rose-500/10 border border-rose-500/20 rounded-2xl p-4 flex flex-col items-center gap-1">
                                <span className="text-2xl font-black text-rose-700">{(results.errors?.length ?? 0) + (results.skipped_invalid ?? 0) + (results.skipped_suppressed ?? 0)}</span>
                                <span className="text-[10px] font-black uppercase tracking-widest text-rose-700 opacity-70">Skipped</span>
                            </div>
                        </div>

                        {results.warnings?.length > 0 && (
                            <div className="w-full bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 text-left mb-6">
                                <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-2 flex items-center gap-2">
                                    <AlertTriangle className="w-3.5 h-3.5" /> {results.warnings.length} Verification Warning{results.warnings.length > 1 ? 's' : ''}
                                </p>
                                <p className="text-xs text-amber-700/70 font-medium">Some contacts have unverified or risky email addresses. They were added but may affect deliverability.</p>
                            </div>
                        )}

                        <Button
                            onClick={() => { if (onComplete) onComplete(); onClose(); }}
                            className="w-full h-13 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-primary/20"
                        >
                            Back to List
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ─── ADDING step ──────────────────────────────────────────────────────
    if (step === 'ADDING') {
        return (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="bg-card border-2 border-border/40 w-full max-w-sm rounded-[2.5rem] p-12 shadow-[0_40px_100px_rgba(0,0,0,0.1)] flex flex-col items-center text-center animate-in zoom-in-95 duration-400">
                    <Loader2 className="w-12 h-12 text-primary animate-spin mb-6" />
                    <h3 className="text-lg font-black tracking-tight mb-1">Adding Contacts...</h3>
                    <p className="text-muted-foreground text-sm">Adding {workspaceContactIds.length} contact{workspaceContactIds.length !== 1 ? 's' : ''} to <span className="font-black">{selectedCampaign?.name}</span></p>
                </div>
            </div>
        );
    }

    // ─── CREATE step ──────────────────────────────────────────────────────
    if (step === 'CREATE') {
        return (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="bg-card border-2 border-border/40 w-full max-w-md rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.1)] overflow-hidden animate-in zoom-in-95 duration-400">
                    <div className="p-7 border-b border-border/40 flex items-center justify-between bg-muted/10">
                        <div className="flex items-center gap-3">
                            <button onClick={() => setStep('SELECT')} className="p-2 hover:bg-muted rounded-xl transition-colors">
                                <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                            </button>
                            <div>
                                <h3 className="text-base font-black tracking-tight">Create Campaign</h3>
                                <p className="text-muted-foreground text-[10px] uppercase font-black tracking-widest mt-0.5">New Outreach Campaign</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2.5 hover:bg-destructive hover:text-white rounded-xl transition-all group">
                            <X className="w-4 h-4 group-hover:rotate-90 transition-transform" />
                        </button>
                    </div>

                    <div className="p-7 space-y-5">
                        <div>
                            <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2">Campaign Name <span className="text-destructive">*</span></label>
                            <input
                                type="text"
                                value={newName}
                                autoFocus
                                onChange={e => { setNewName(e.target.value); setCreateError(''); }}
                                onKeyDown={e => e.key === 'Enter' && handleCreateCampaign()}
                                placeholder="e.g. Q3 SaaS Outreach"
                                className="w-full bg-muted/30 border-2 border-border/50 text-foreground rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all outline-none text-sm"
                            />
                            {createError && (
                                <p className="text-xs text-destructive font-bold mt-2 flex items-center gap-1.5">
                                    <AlertTriangle className="w-3 h-3" /> {createError}
                                </p>
                            )}
                        </div>

                        <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-4 flex items-start gap-3">
                            <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-700 font-semibold leading-relaxed">
                                This creates a <span className="font-black">DRAFT</span> campaign. You can configure template, sender, and scheduling later from the Campaigns page. Selected contacts will be immediately queued.
                            </p>
                        </div>
                    </div>

                    <div className="px-7 pb-7 flex gap-3">
                        <Button variant="ghost" onClick={() => setStep('SELECT')} className="flex-1 h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 border-border/40">
                            Cancel
                        </Button>
                        <Button
                            disabled={isCreating || !newName.trim()}
                            onClick={handleCreateCampaign}
                            className="flex-[2] h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
                        >
                            {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                            {isCreating ? 'Creating...' : 'Create & Select'}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ─── SELECT step (default) ────────────────────────────────────────────
    return (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-card border-2 border-border/40 w-full max-w-lg rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.1)] overflow-hidden animate-in zoom-in-95 duration-400 flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="p-7 border-b border-border/40 flex items-center justify-between bg-muted/10 shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 bg-indigo-500/10 rounded-2xl flex items-center justify-center text-indigo-600 shadow-inner">
                            <Rocket className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-lg font-black tracking-tight">Add to Campaign</h3>
                            <p className="text-muted-foreground text-[10px] uppercase font-black tracking-widest mt-0.5">
                                {workspaceContactIds.length} Contact{workspaceContactIds.length !== 1 ? 's' : ''} Selected
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2.5 hover:bg-destructive hover:text-white rounded-xl transition-all group">
                        <X className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                    </button>
                </div>

                {/* Selected contacts badge */}
                <div className="px-7 pt-5 shrink-0">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Choose Campaign</span>
                        <button
                            onClick={() => setStep('CREATE')}
                            className="flex items-center gap-1.5 text-[10px] font-black text-primary uppercase tracking-widest hover:text-primary/80 transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" /> Create New
                        </button>
                    </div>

                    {/* Search */}
                    <div className="relative mb-4">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search campaigns..."
                            className="w-full h-11 pl-11 pr-4 bg-muted/30 border-2 border-border/50 rounded-2xl font-semibold text-sm outline-none focus:border-primary transition-colors"
                        />
                    </div>
                </div>

                {/* Campaign list */}
                <div className="px-7 overflow-y-auto flex-1 pb-2">
                    {loadingCampaigns ? (
                        <div className="py-12 flex items-center justify-center">
                            <Loader2 className="w-8 h-8 text-primary animate-spin opacity-40" />
                        </div>
                    ) : filteredCampaigns.length === 0 ? (
                        <div className="py-12 text-center">
                            <Rocket className="w-10 h-10 mx-auto mb-3 opacity-20" />
                            <p className="text-sm font-black text-muted-foreground opacity-50 uppercase tracking-widest">
                                {search ? 'No campaigns match your search' : 'No active campaigns'}
                            </p>
                            <button
                                onClick={() => setStep('CREATE')}
                                className="mt-4 text-xs font-black text-primary uppercase tracking-widest hover:underline flex items-center gap-1.5 mx-auto"
                            >
                                <Plus className="w-3.5 h-3.5" /> Create your first campaign
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredCampaigns.map(campaign => {
                                const isSelected = selectedCampaignId === campaign._id;
                                return (
                                    <button
                                        key={campaign._id}
                                        onClick={() => setSelectedCampaignId(campaign._id)}
                                        className={`w-full text-left flex items-center justify-between p-4 rounded-2xl border-2 transition-all group ${
                                            isSelected
                                                ? 'bg-indigo-500/5 border-indigo-500/30 shadow-md ring-4 ring-indigo-500/5'
                                                : 'bg-muted/20 border-transparent hover:bg-muted/40 hover:border-border/50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all ${isSelected ? 'bg-indigo-600 text-white shadow-lg' : 'bg-background border-2 border-border/40 text-muted-foreground'}`}>
                                                <Zap className="w-4 h-4" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className={`font-black text-sm truncate ${isSelected ? 'text-indigo-700' : 'text-foreground'}`}>
                                                    {campaign.name}
                                                </p>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${campaignStatusColor(campaign.status)}`}>
                                                        {campaign.status}
                                                    </span>
                                                    {campaign.templateId && (
                                                        <span className="text-[10px] text-muted-foreground font-medium truncate">
                                                            {campaign.templateId.name}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {isSelected && (
                                            <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center shrink-0 ml-3">
                                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-7 border-t border-border/40 bg-muted/10 shrink-0 flex gap-4">
                    <Button variant="ghost" onClick={onClose} className="flex-1 h-13 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 border-border/40 hover:bg-background">
                        Cancel
                    </Button>
                    <Button
                        disabled={!selectedCampaignId || workspaceContactIds.length === 0}
                        onClick={handleAddContacts}
                        className="flex-[2] h-13 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-500/20 bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center gap-2 border-0"
                    >
                        <ChevronRight className="w-4 h-4" />
                        Add {workspaceContactIds.length} Contact{workspaceContactIds.length !== 1 ? 's' : ''}
                    </Button>
                </div>
            </div>
        </div>
    );
}
